import type { ReactNode } from 'react';

interface CardProps {
  title?: string;
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  /** When true, header becomes a toggle button (mobile only — desktop ignores collapse state) */
  collapsible?: boolean;
  /** One-line summary shown in the collapsed header on mobile */
  summary?: string;
  isOpen?: boolean;
  onToggle?: () => void;
}

export function Card({ title, children, className = '', style, collapsible, summary, isOpen = true, onToggle }: CardProps) {
  const header = collapsible && title ? (
    <button
      type="button"
      className="card-header card-header-toggle"
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <span>{title}</span>
      {!isOpen && summary && <span className="card-header-summary">{summary}</span>}
      <span className={`card-chevron${isOpen ? ' open' : ''}`}>❯</span>
    </button>
  ) : title ? (
    <div className="card-header">{title}</div>
  ) : null;

  return (
    <div
      className={`card ${className}`}
      style={style}
      data-open={collapsible ? String(isOpen) : undefined}
    >
      {header}
      <div className="card-body">{children}</div>
    </div>
  );
}

interface FormGroupProps {
  label: string;
  htmlFor?: string;
  hint?: string;
  full?: boolean;
  children: ReactNode;
}

export function FormGroup({ label, htmlFor, hint, full, children }: FormGroupProps) {
  return (
    <div className={`form-group${full ? ' full' : ''}`}>
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

interface InputWithUnitProps {
  unit: string;
  unitPosition?: 'left' | 'right';
  value: number | string;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number | string;
  id?: string;
  readOnly?: boolean;
}

export function InputWithUnit({
  unit,
  unitPosition = 'left',
  value,
  onChange,
  min,
  max,
  step,
  id,
  readOnly,
}: InputWithUnitProps) {
  const isRight = unitPosition === 'right';
  // Use text input for readonly fields so formatted strings (e.g. "315,000") display correctly
  const inputType = readOnly ? 'text' : 'number';
  return (
    <div className="input-with-unit">
      <span className={`unit${isRight ? ' unit-right' : ''}`}>{unit}</span>
      <input
        type={inputType}
        id={id}
        value={value}
        onFocus={e => e.target.select()}
        onChange={e => onChange(parseFloat(e.target.value) || 0)}
        min={readOnly ? undefined : min}
        max={readOnly ? undefined : max}
        step={readOnly ? undefined : step}
        className={isRight ? 'has-right-unit' : ''}
        readOnly={readOnly}
        tabIndex={readOnly ? -1 : undefined}
        style={readOnly ? { background: 'var(--section-bg)', cursor: 'default' } : undefined}
      />
    </div>
  );
}

interface ToggleProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  id?: string;
}

export function Toggle({ label, checked, onChange, id }: ToggleProps) {
  return (
    <div className="toggle-row">
      <label className="toggle">
        <input
          type="checkbox"
          id={id}
          checked={checked}
          onChange={e => onChange(e.target.checked)}
        />
        <span className="slider" />
      </label>
      <span className="toggle-label">{label}</span>
    </div>
  );
}
